import React, { useState, useEffect, useCallback, memo } from "react";
import {
  FaExternalLinkAlt,
  FaTimes,
  FaChevronLeft,
  FaChevronRight,
  FaArrowRight,
  FaFilter,
  FaLaptopCode,
} from "react-icons/fa";
import useScrollTo from "../hooks/useScrollTo";
import portfolioData from "./portfolio.json";
import { getProjectImage } from "./images";
import SEOHelmet from "../components/SEO/SEOHelmet";
import { BreadcrumbSchema } from "../components/SEO/schemas";

// Category color mapping
const categoryColors = {
  "Government & Civic": "from-blue-600 to-cyan-500",
  "Business & Commerce": "from-purple-600 to-indigo-500",
  "SaaS & Tools": "from-orange-500 to-amber-500",
  "Nonprofit & Community": "from-teal-500 to-green-500",
  "Insurance & Finance": "from-rose-500 to-pink-500",
  "Travel & Logistics": "from-sky-500 to-blue-500",
  Healthcare: "from-emerald-500 to-teal-500",
};

const getColor = (category) =>
  categoryColors[category] || "from-primary to-teal";

// Lightbox Modal - reused from OurWork but self-contained here
const ProjectModal = memo(({ project, allProjects, onClose, onNavigate }) => {
  const imageSrc = getProjectImage(project.image);
  const color = getColor(project.category);
  const currentIndex = allProjects.findIndex((p) => p.id === project.id);
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < allProjects.length - 1;

  const goPrev = useCallback(() => {
    if (hasPrev) onNavigate(allProjects[currentIndex - 1]);
  }, [hasPrev, allProjects, currentIndex, onNavigate]);

  const goNext = useCallback(() => {
    if (hasNext) onNavigate(allProjects[currentIndex + 1]);
  }, [hasNext, allProjects, currentIndex, onNavigate]);

  useEffect(() => {
    const handleKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") goPrev();
      if (e.key === "ArrowRight") goNext();
    };
    document.addEventListener("keydown", handleKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = "";
    };
  }, [onClose, goPrev, goNext]);

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 md:p-8"
      role="dialog"
      aria-modal="true"
      aria-label={project.title}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm portfolio-modal-enter"
        onClick={onClose}
      />

      {/* Wrapper: constrains arrow positioning to modal width */}
      <div className="relative z-10 w-full max-w-5xl">

        {/* Modal — first in DOM */}
        <div className="relative w-full max-h-[90vh] bg-white dark:bg-gray-900 rounded-2xl shadow-2xl overflow-hidden portfolio-modal-content-enter flex flex-col">
          <div className={`h-1.5 bg-gradient-to-r ${color} flex-shrink-0`}></div>

          {/* Close */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2.5 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 hover:rotate-90 group"
            aria-label="Close"
          >
            <FaTimes className="text-gray-600 dark:text-gray-300 group-hover:text-red-500 transition-colors duration-300" />
          </button>

          <div className="overflow-y-auto flex-1">
            {imageSrc && (
              <div className="relative w-full bg-gray-100 dark:bg-gray-800">
                <img
                  src={imageSrc}
                  alt={project.title}
                  className="w-full max-h-[45vh] object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white dark:from-gray-900 via-transparent to-transparent"></div>
              </div>
            )}

            <div className="p-6 md:p-8 -mt-8 relative">
              {/* Tags */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <span
                  className={`inline-flex px-3 py-1 text-xs font-bold rounded-full bg-gradient-to-r ${color} text-white shadow-sm`}
                >
                  {project.category}
                </span>
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                  {project.type}
                </span>
                <span className="px-3 py-1 text-xs font-semibold rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 border border-gray-200 dark:border-gray-700">
                  {project.year}
                </span>
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white mb-3">
                {project.title}
              </h3>

              <p className="text-gray-600 dark:text-gray-400 leading-relaxed text-base md:text-lg mb-6">
                {project.description}
              </p>

              {/* Details grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Tech Stack
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {(project.techStack || []).map((tech) => (
                      <span
                        key={tech}
                        className="px-2.5 py-1 text-xs font-medium bg-white dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md border border-gray-200 dark:border-gray-600 shadow-sm"
                      >
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 dark:bg-gray-800/50 p-4 rounded-xl border border-gray-100 dark:border-gray-700">
                  <h4 className="text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                    Our Role
                  </h4>
                  <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                    {project.role || "Full-Stack Development"}
                  </p>
                </div>
              </div>

              {project.url && project.url !== "#" ? (
                <a
                  href={project.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                >
                  Visit Live Site
                  <FaExternalLinkAlt className="text-sm group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-300" />
                </a>
              ) : (
                <span className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-semibold rounded-xl border border-gray-200 dark:border-gray-700">
                  Private Project
                </span>
              )}
            </div>
          </div>

          {/* Counter */}
          <div className="flex-shrink-0 px-6 py-3 bg-gray-50 dark:bg-gray-800 border-t border-gray-100 dark:border-gray-700 text-center">
            <span className="text-xs font-medium text-gray-400 dark:text-gray-500">
              {currentIndex + 1} of {allProjects.length}
            </span>
          </div>
        </div>

        {/* Arrow overlay — after modal in DOM so it naturally paints on top.
            pointer-events-none lets scrolling/clicks pass through to the modal;
            pointer-events-auto re-enables only the arrow buttons. */}
        <div className="absolute inset-0 pointer-events-none flex items-center justify-between px-3">
          {hasPrev ? (
            <button
              onClick={goPrev}
              className="pointer-events-auto p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 group"
              aria-label="Previous project"
            >
              <FaChevronLeft className="text-gray-600 dark:text-gray-300 group-hover:text-primary dark:group-hover:text-teal transition-colors duration-300" />
            </button>
          ) : <div />}
          {hasNext ? (
            <button
              onClick={goNext}
              className="pointer-events-auto p-3 bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm rounded-full shadow-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-300 group"
              aria-label="Next project"
            >
              <FaChevronRight className="text-gray-600 dark:text-gray-300 group-hover:text-primary dark:group-hover:text-teal transition-colors duration-300" />
            </button>
          ) : <div />}
        </div>

      </div>
    </div>
  );
});

// Add displayName for better debugging in React DevTools
ProjectModal.displayName = "ProjectModal";

const OurWorkPage = () => {
  const { scrollToSection } = useScrollTo();
  const [activeFilter, setActiveFilter] = useState("All");
  const [modalProject, setModalProject] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Extract unique categories
  const categories = [
    "All",
    ...new Set(portfolioData.projects.map((p) => p.category)),
  ];

  const filteredProjects =
    activeFilter === "All"
      ? portfolioData.projects
      : portfolioData.projects.filter((p) => p.category === activeFilter);

  const openModal = useCallback((project) => setModalProject(project), []);
  const closeModal = useCallback(() => setModalProject(null), []);
  const navigateModal = useCallback((project) => setModalProject(project), []);

  return (
    <>
      {/* SEO Meta Tags */}
      <SEOHelmet
        title="Our Work | CitiTekPro Portfolio - Minnesota Software Development Projects"
        description="Explore CitiTekPro's portfolio of custom software development, web development, and mobile app projects for businesses in Minnesota. View our completed work across government, healthcare, commerce, and nonprofit sectors."
        keywords="CitiTekPro portfolio, Minnesota software projects, web development case studies, custom software examples, MERN stack projects, business automation projects Minnesota"
        canonicalUrl="https://cititekpro.com/our-work"
        ogType="website"
      />
      <BreadcrumbSchema
        breadcrumbs={[
          { name: 'Home', url: 'https://cititekpro.com/' },
          { name: 'Our Work', url: 'https://cititekpro.com/our-work' }
        ]}
      />

      <div className="relative pt-16 min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden">
        {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 -left-40 w-96 h-96 bg-primary/5 dark:bg-teal/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-40 -right-40 w-96 h-96 bg-teal/5 dark:bg-primary/5 rounded-full filter blur-3xl"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-20">
        {/* Page Header */}
        <div className="text-center mb-10 md:mb-14 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 dark:bg-teal/10 rounded-full mb-6">
            <FaLaptopCode className="text-primary dark:text-teal" />
            <span className="text-sm font-semibold text-primary dark:text-teal">
              Full Portfolio
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-4">
            <span className="text-gray-900 dark:text-white">Our </span>
            <span className="gradient-text">Work</span>
          </h1>
          <div className="h-1.5 w-24 mx-auto bg-gradient-to-r from-primary via-teal to-accent rounded-full mb-6"></div>
          <p className="text-lg md:text-xl text-gray-600 dark:text-gray-400 max-w-3xl mx-auto leading-relaxed">
            Browse some of our recent completed portfolio of{" "}
            {portfolioData.projects.length} projects across{" "}
            {categories.length - 1} industries. Click any project to explore the
            details.
          </p>
        </div>

        {/* Category Filter */}
        <div
          className="flex flex-wrap items-center justify-center gap-2 mb-10 animate-fade-in-up"
          style={{ animationDelay: "0.15s" }}
        >
          <FaFilter className="text-gray-400 dark:text-gray-500 mr-1 text-sm" />
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveFilter(cat)}
              className={`px-4 py-2 text-sm font-medium rounded-xl transition-all duration-300 ${
                activeFilter === cat
                  ? "bg-gradient-to-r from-primary to-teal text-white shadow-lg shadow-primary/20 dark:shadow-teal/20 scale-105"
                  : "bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-200 dark:border-gray-700 hover:border-primary/30 dark:hover:border-teal/30"
              }`}
            >
              {cat}
              {cat !== "All" && (
                <span
                  className={`ml-1.5 text-xs ${activeFilter === cat ? "text-white/70" : "text-gray-400"}`}
                >
                  (
                  {
                    portfolioData.projects.filter((p) => p.category === cat)
                      .length
                  }
                  )
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Results count */}
        <p className="text-center text-sm text-gray-500 dark:text-gray-400 mb-8">
          Showing {filteredProjects.length} project
          {filteredProjects.length !== 1 ? "s" : ""}
          {activeFilter !== "All" && ` in ${activeFilter}`}
        </p>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-16">
          {filteredProjects.map((project, index) => {
            const imageSrc = getProjectImage(project.image);
            const color = getColor(project.category);

            return (
              <button
                key={project.id}
                onClick={() => openModal(project)}
                className="group relative bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden border border-gray-100 dark:border-gray-700 card-hover animate-fade-in-up flex flex-col text-left focus:outline-none focus:ring-2 focus:ring-primary dark:focus:ring-teal focus:ring-offset-2 dark:focus:ring-offset-gray-900"
                style={{ animationDelay: `${index * 0.08}s` }}
                aria-label={`View ${project.title} details`}
              >
                {/* Screenshot */}
                {imageSrc && (
                  <div className="relative overflow-hidden aspect-[16/10]">
                    <img
                      src={imageSrc}
                      alt={project.title}
                      className="w-full h-full object-cover object-top group-hover:scale-110 transition-transform duration-700 ease-out"
                      loading="lazy"
                    />
                    {/* Hover overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 flex items-end justify-center pb-5">
                      <span className="px-5 py-2.5 bg-white/95 dark:bg-gray-900/95 backdrop-blur-sm text-primary dark:text-teal font-semibold rounded-xl shadow-xl flex items-center gap-2 text-sm transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                        View Details
                        <FaArrowRight className="text-xs" />
                      </span>
                    </div>
                    {/* Category badge */}
                    <div className="absolute top-3 left-3">
                      <span
                        className={`inline-flex px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider rounded-lg bg-gradient-to-r ${color} text-white shadow-md`}
                      >
                        {project.category}
                      </span>
                    </div>
                    {/* Year badge */}
                    <div className="absolute top-3 right-3">
                      <span className="px-2 py-1 text-[10px] font-bold bg-black/50 backdrop-blur-sm text-white rounded-lg">
                        {project.year}
                      </span>
                    </div>
                  </div>
                )}

                <div className="p-5 flex flex-col flex-grow">
                  {/* Title */}
                  <h3 className="text-base md:text-lg font-bold text-gray-900 dark:text-white mb-1.5 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-primary group-hover:to-teal dark:group-hover:from-teal dark:group-hover:to-primary transition-all duration-300 truncate">
                    {project.title}
                  </h3>

                  {/* Type */}
                  <p className="text-xs text-gray-400 dark:text-gray-500 mb-2 font-medium">
                    {project.type}
                  </p>

                  {/* Description */}
                  <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed mb-4 line-clamp-2 flex-grow">
                    {project.description}
                  </p>

                  {/* Tech Stack */}
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {(project.techStack || []).slice(0, 3).map((tech) => (
                      <span
                        key={tech}
                        className="px-2 py-0.5 text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 rounded-md"
                      >
                        {tech}
                      </span>
                    ))}
                    {(project.techStack || []).length > 3 && (
                      <span className="px-2 py-0.5 text-[10px] font-semibold bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 rounded-md">
                        +{project.techStack.length - 3}
                      </span>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100 dark:border-gray-700">
                    {project.url && project.url !== "#" ? (
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-primary dark:text-teal">
                        Live Site
                        <FaExternalLinkAlt className="text-[9px]" />
                      </span>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                        Private
                      </span>
                    )}
                    <span className="text-xs text-gray-400 dark:text-gray-500 font-medium">
                      {project.role?.split("&")[0]?.trim() || "Development"}
                    </span>
                  </div>
                </div>

                {/* Decorative */}
                <div
                  className={`absolute -bottom-6 -right-6 w-20 h-20 bg-gradient-to-br ${color} opacity-0 group-hover:opacity-10 rounded-full filter blur-xl transition-all duration-500 group-hover:scale-150`}
                ></div>
              </button>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="text-center bg-gradient-to-r from-primary/10 via-teal/10 to-accent/10 dark:from-primary/20 dark:via-teal/20 dark:to-accent/20 p-8 md:p-12 rounded-3xl backdrop-blur-sm animate-fade-in-up border border-gray-200 dark:border-gray-700">
          <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Have a Project in Mind?
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-8 text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
            Let's discuss how we can build the right solution for your business.
            Schedule a free consultation to get started.
          </p>
          <button
            onClick={() => scrollToSection("consultation")}
            className="group relative px-8 py-4 bg-gradient-to-r from-primary to-teal hover:from-primary-600 hover:to-teal-600 text-white font-bold rounded-xl shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden"
          >
            <span className="relative z-10 flex items-center gap-2">
              Get Free Consultation
              <svg
                className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </span>
            <div className="absolute inset-0 bg-white/20 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left"></div>
          </button>
        </div>
      </div>

      {/* Lightbox Modal */}
      {modalProject && (
        <ProjectModal
          project={modalProject}
          allProjects={filteredProjects}
          onClose={closeModal}
          onNavigate={navigateModal}
        />
      )}
    </div>
    </>
  );
};

export default OurWorkPage;
